/**
 * useUserTier — Reads the current user's business tier.
 *
 * During beta: all users are treated as premium.
 * Admins always have full access regardless of beta mode.
 *
 * Extended with PerformTier (free/lifetime/subscriber) for the
 * 3-tier access system. During beta, performTier defaults to 'subscriber'.
 *
 * @since Business Tier V1 foundation
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isFlagAccessibleForTier, type PerformTier } from "@/constants/tiers";

export type BusinessTier = "free" | "premium" | "coach";

export interface UserTierData {
  tier: BusinessTier;
  validFrom: string;
  validUntil: string | null;
  hasStripe: boolean;
}

// ── Local DB row type ──────────────────────────────────────────

interface UserTierRow {
  tier: string;
  valid_from: string;
  valid_until: string | null;
  stripe_customer_id: string | null;
}

// ── Typed query helper ─────────────────────────────────────────

function tiersTable() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return supabase.from("user_tiers" as any);
}

// ── Defaults ───────────────────────────────────────────────────

const DEFAULT_TIER: UserTierData = {
  tier: "free",
  validFrom: new Date().toISOString(),
  validUntil: null,
  hasStripe: false,
};

interface TierWithRole {
  tierData: UserTierData;
  isAdmin: boolean;
  rawTier: string;
}

/**
 * Map raw DB tier string to PerformTier.
 * - 'free' or unknown → 'free'
 * - 'premium' or 'pro' → 'lifetime'
 * - 'subscriber' → 'subscriber'
 */
function mapToPerformTier(raw: string): PerformTier {
  if (raw === "subscriber") return "subscriber";
  if (raw === "premium" || raw === "pro" || raw === "lifetime") return "lifetime";
  return "free";
}

export function useUserTier() {
  const { user } = useAuth();
  const userId = user?.id;

  const { data, isLoading } = useQuery({
    queryKey: userId ? ["user-tier-and-role", userId] : ["user-tier-and-role", "none"],
    queryFn: async (): Promise<TierWithRole> => {
      if (!userId) return { tierData: DEFAULT_TIER, isAdmin: false, rawTier: "free" };

      // Fetch tier and roles in parallel
      const [tierResult, rolesResult] = await Promise.all([
        tiersTable()
          .select("tier, valid_from, valid_until, stripe_customer_id")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase.rpc("get_user_roles", { _user_id: userId }),
      ]);

      // Parse tier
      let tierData = DEFAULT_TIER;
      let rawTier = "free";
      if (!tierResult.error && tierResult.data) {
        const r = tierResult.data as unknown as UserTierRow;
        rawTier = r.tier;
        tierData = {
          tier: r.tier as BusinessTier,
          validFrom: r.valid_from,
          validUntil: r.valid_until,
          hasStripe: !!r.stripe_customer_id,
        };
      }

      // Parse roles
      let isAdmin = false;
      if (!rolesResult.error && rolesResult.data) {
        const parsed = typeof rolesResult.data === "string"
          ? JSON.parse(rolesResult.data)
          : rolesResult.data;
        isAdmin = !!parsed.is_admin || !!parsed.is_super_admin;
      }

      return { tierData, isAdmin, rawTier };
    },
    enabled: !!userId,
    staleTime: 60_000,
  });

  const tierData = data?.tierData ?? DEFAULT_TIER;
  const isAdmin = data?.isAdmin ?? false;
  const rawTier = data?.rawTier ?? "free";

  // Beta mode: everyone gets premium access
  // Admin bypass: always premium regardless of beta
  const effectiveTier = "premium" as BusinessTier;

  // ── PerformTier ──
  // During beta: all users get 'subscriber' access
  const actualPerformTier = mapToPerformTier(rawTier);
  const performTier: PerformTier = "subscriber"; // beta override

  return {
    // Existing fields — unchanged
    tier: effectiveTier,
    isPremium: true,
    isCoach: tierData.tier === "coach",
    isFree: false,
    isAdmin,
    isBetaAccess: !isAdmin && tierData.tier === "free",
    validUntil: tierData.validUntil,
    hasStripe: tierData.hasStripe,
    loading: isLoading,

    // New 3-tier fields
    performTier,
    actualPerformTier, // real DB tier (for display/debug)
    isLifetime: (performTier as string) === "lifetime" || (performTier as string) === "subscriber",
    isSubscriber: (performTier as string) === "subscriber",
    canAccessFlag: (flag: string) => isFlagAccessibleForTier(flag, performTier),
  };
}
